export const ADVICE_STATES = {
  accepted: '已提交，等待后台处理',
  pending: '等待处理',
  running: '正在生成建议',
  failed: '建议生成失败',
  succeeded: '建议已生成',
  not_found: '当前计划尚无训练建议',
  disabled: '训练建议尚未启用',
};
export const ADVICE_FAILURES = {
  PROVIDER_ERROR: '模型服务异常',
  EXECUTION_TIMEOUT: '模型执行超时',
  INVALID_SNAPSHOT: '证据快照校验失败',
  INVALID_OUTPUT: '建议协议校验失败',
  INTERNAL_ERROR: '内部处理异常',
  STALE_LEASE: '上次执行超时，任务已回收',
  CAPACITY_DEFERRED: '模型并发繁忙，已延后执行',
};
export const ADVICE_INTENTS = {
  CHECK_EVIDENCE: '继续核对',
  ADJUSTMENT_DIRECTION: '调整方向',
};
export const FOLLOW_UP_STATES = {
  NEW_EVIDENCE: '已有后续新证据',
  INSUFFICIENT_EVIDENCE: '后续证据仍不足',
};
// 与 Detail TrainingAdviceProtocol 对齐的协议版本常量。
// 提示词版本独立于结果 schema 演化；旧值 evidence@1 表示上游未随协议升级，必须拒绝而不是展示。
export const TRAINING_ADVICE_RESULT_SCHEMA = 'training-advice-result@1';
export const TRAINING_ADVICE_PROMPT_VERSION = 'training-advice-prompt@3';
const SOURCE_TYPES = new Set([
  'COMMUNICATION_OBSERVATION',
  'COURSE_FACT',
  'COURSE_ASSESSMENT',
  'ACTION_ASSESSMENT',
  'PLAN_ANALYSIS',
  'MEMBER_CHANGE',
]);
const ASSERTIONS = new Set([
  'MEMBER_REPORTED',
  'COACH_REPORTED',
  'DETERMINISTIC',
  'AI_INTERPRETATION',
  'UNKNOWN',
]);
const EVIDENCE_STATES = new Set(['CURRENT', 'NEEDS_CONFIRMATION']);
const AI_SOURCE_TYPES = new Set([
  'COURSE_ASSESSMENT',
  'ACTION_ASSESSMENT',
  'PLAN_ANALYSIS',
  'MEMBER_CHANGE',
]);
const uuid = (value) =>
  typeof value === 'string' &&
  /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(
    value,
  );
const id = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0;
const time = (value) => Number.isSafeInteger(value) && value > 0;
const text = (value, max) =>
  typeof value === 'string' && !!value.trim() && value.length <= max;
const list = (value, max) => Array.isArray(value) && value.length <= max;
const strings = (value, min, max, len) =>
  list(value, max) &&
  value.length >= min &&
  value.every((item) => text(item, len)) &&
  new Set(value).size === value.length;
const fail = () => {
  throw new Error('Invalid training advice response');
};
const exact = (value, keys) =>
  value &&
  !Array.isArray(value) &&
  Object.keys(value).length === keys.length &&
  keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));

export function readTrainingAdvice(response, query, submitting = false) {
  const data = response?.data;
  const memberId = Number(query.memberId);
  const planId = Number(query.planId);
  if (
    !id(memberId) ||
    !id(planId) ||
    (query.taskId != null && !uuid(query.taskId)) ||
    !['0', '00000'].includes(String(response?.code)) ||
    data?.schemaVersion !== 'training-advice-task-view@1' ||
    data.memberId !== memberId ||
    data.planId !== planId ||
    typeof data.generationEnabled !== 'boolean' ||
    !(
      submitting
        ? ['accepted', 'disabled']
        : ['disabled', 'not_found', 'pending', 'running', 'failed', 'succeeded']
    ).includes(data.status)
  )
    fail();
  const out = {
    status: data.status,
    generationEnabled: data.generationEnabled,
    memberId,
    planId,
  };
  if (['disabled', 'not_found'].includes(data.status)) return out;
  if (!uuid(data.taskId) || (query.taskId && query.taskId !== data.taskId))
    fail();
  out.taskId = data.taskId;
  if (submitting) return out;
  if (
    !Number.isInteger(data.attemptCount) ||
    data.attemptCount < 0 ||
    data.attemptCount > 3 ||
    !time(data.capturedAt) ||
    !time(data.createdAt) ||
    !time(data.updatedAt) ||
    data.updatedAt < data.createdAt ||
    ['nextExecuteAt', 'startedAt', 'finishedAt'].some(
      (key) => data[key] != null && !time(data[key]),
    ) ||
    (data.failureCode != null && !ADVICE_FAILURES[data.failureCode])
  )
    fail();
  if (
    (data.status === 'pending' &&
      (!data.nextExecuteAt || data.finishedAt != null)) ||
    (data.status === 'running' &&
      (!data.startedAt ||
        data.nextExecuteAt != null ||
        data.finishedAt != null)) ||
    (['failed', 'succeeded'].includes(data.status) && !data.finishedAt)
  )
    fail();
  [
    'attemptCount',
    'capturedAt',
    'createdAt',
    'updatedAt',
    'nextExecuteAt',
    'startedAt',
    'finishedAt',
    'failureCode',
  ].forEach((key) => {
    out[key] = data[key];
  });
  if (data.status !== 'succeeded') return out;
  if (
    !text(data.model, 128) ||
    !/^[A-Za-z0-9._:/-]+$/.test(data.model) ||
    typeof data.resultJson !== 'string' ||
    new Blob([data.resultJson]).size > 24000
  )
    fail();
  const result = JSON.parse(data.resultJson);
  if (
    !exact(result, [
      'schemaVersion',
      'promptVersion',
      'processingStatus',
      'implementationStatus',
      'causalAttribution',
      'memberId',
      'planId',
      'capturedAt',
      'missingEvidence',
      'evidenceHash',
      'suggestions',
      'followUps',
      'evidenceSources',
    ]) ||
    result?.schemaVersion !== TRAINING_ADVICE_RESULT_SCHEMA ||
    result.promptVersion !== TRAINING_ADVICE_PROMPT_VERSION ||
    result.processingStatus !== 'analyzed' ||
    result.implementationStatus !== 'UNVERIFIED' ||
    result.causalAttribution !== 'NOT_ESTABLISHED' ||
    result.memberId !== memberId ||
    result.planId !== planId ||
    result.capturedAt !== data.capturedAt ||
    !/^[a-f0-9]{64}$/.test(result.evidenceHash) ||
    !strings(result.missingEvidence, 0, 12, 160) ||
    !list(result.suggestions, 5) ||
    !list(result.followUps, 5) ||
    !list(result.evidenceSources, 24)
  )
    fail();
  const sources = new Map();
  result.evidenceSources.forEach((source) => {
    if (
      !source ||
      !exact(source, [
        'ref',
        'sourceType',
        'sourceId',
        'occurredAt',
        'assertion',
        'state',
        'text',
        ...(source.planId == null ? [] : ['planId']),
      ]) ||
      !/^evidence\.(?:[1-9]|1[0-9]|2[0-4])$/.test(source.ref) ||
      sources.has(source.ref) ||
      !text(source.text, 400) ||
      !text(source.sourceId, 128) ||
      !SOURCE_TYPES.has(source.sourceType) ||
      !ASSERTIONS.has(source.assertion) ||
      (AI_SOURCE_TYPES.has(source.sourceType) &&
        source.assertion !== 'AI_INTERPRETATION') ||
      !EVIDENCE_STATES.has(source.state) ||
      !time(source.occurredAt) ||
      source.occurredAt > result.capturedAt ||
      (source.planId != null && source.planId !== planId)
    )
      fail();
    sources.set(source.ref, source);
  });
  const used = new Set();
  const adviceIds = new Set();
  result.suggestions.forEach((item) => {
    if (
      !item ||
      !exact(item, [
        'intent',
        'direction',
        'reason',
        'expectedObservation',
        'limitations',
        'evidenceRefs',
        'adviceId',
      ]) ||
      !ADVICE_INTENTS[item.intent] ||
      !/^advice\.[a-f0-9]{64}$/.test(item.adviceId) ||
      adviceIds.has(item.adviceId) ||
      !text(item.direction, 240) ||
      !text(item.reason, 240) ||
      !text(item.expectedObservation, 240) ||
      !strings(item.limitations, 1, 3, 160) ||
      !strings(item.evidenceRefs, 1, 4, 32)
    )
      fail();
    adviceIds.add(item.adviceId);
    item.evidenceRefs.forEach((ref) => {
      if (!sources.has(ref)) fail();
      used.add(ref);
    });
  });
  const followUpIds = new Set();
  result.followUps.forEach((item) => {
    if (
      !item ||
      !exact(item, ['adviceId', 'status', 'summary', 'evidenceRefs']) ||
      !/^advice\.[a-f0-9]{64}$/.test(item.adviceId) ||
      followUpIds.has(item.adviceId) ||
      !FOLLOW_UP_STATES[item.status] ||
      !text(item.summary, 300) ||
      !strings(item.evidenceRefs, item.status === 'NEW_EVIDENCE' ? 1 : 0, 4, 32)
    )
      fail();
    followUpIds.add(item.adviceId);
    item.evidenceRefs.forEach((ref) => {
      if (!sources.has(ref)) fail();
      used.add(ref);
    });
  });
  if (used.size !== sources.size) fail();
  out.model = data.model;
  out.result = result;
  return out;
}
